'use client'
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Loader2,
  Mail,
  AlertCircle,
  Calendar,
  RefreshCw,
  FileText,
  ArrowLeft,
  Settings2,
  Sparkles,
  CheckCircle2,
} from 'lucide-react'
import { toast } from 'sonner'
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





type ModalStep = 'config' | 'prompt' | 'result'

interface Inbox {
  id: string
  address: string
}

interface CachedPrompt {
  prompt: string
  stats: {
    emailsFound: number
    emailsProcessed: number
    threadsAnalyzed: number
    tokensUsed: number
  }
  generatedAt: string
  expiresAt: string
  inboxId: string
}

interface GeneratePromptModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onApplyPrompt: (prompt: string) => void
}

export function GeneratePromptModal({
  open,
  onOpenChange,
  onApplyPrompt,
}: GeneratePromptModalProps) {
  const {locale: uiFormattingLocale} = useUiControlLabels()



  const tUi = useTranslations("Email.AgentsEmailDialogsGeneratePromptModal")
  const tCommonUi = useTranslations("Common")

  const { fetchWithAuth } = useAuth()

  const [step, setStep] = useState<ModalStep>('config')
  const [inboxes, setInboxes] = useState<Inbox[]>([])
  const [selectedInboxId, setSelectedInboxId] = useState<string>('')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [maxEmails, setMaxEmails] = useState<string>('1000')
  const [emailCountPreview, setEmailCountPreview] = useState<{ count: number; hasMore: boolean } | null>(null)
  const [isLoadingInboxes, setIsLoadingInboxes] = useState(false)
  const [isLoadingCount, setIsLoadingCount] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)

  // Prompt state
  const [systemPrompt, setSystemPrompt] = useState<string>('')
  const [promptSource, setPromptSource] = useState<'tenant' | 'global' | 'default'>('default')
  // Generated prompt state
  const [generatedPrompt, setGeneratedPrompt] = useState<string>('')
  const [generatedStats, setGeneratedStats] = useState<CachedPrompt['stats'] | null>(null)

  // Cached prompt state
  const [cachedPrompt, setCachedPrompt] = useState<CachedPrompt | null>(null)

  // Set default date to 30 days ago
  useEffect(() => {
    if (open && !dateFrom) {
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
      setDateFrom(thirtyDaysAgo.toISOString().split('T')[0])
    }
  }, [open, dateFrom])

  useEffect(() => {
    if (open) {
      fetchInboxesAndData()
    }
  }, [open])

  // Fetch email count when params change
  useEffect(() => {
    if (selectedInboxId && dateFrom) {
      fetchEmailCount()
    } else {
      setEmailCountPreview(null)
    }
  }, [selectedInboxId, dateFrom, dateTo])

  const fetchInboxesAndData = async () => {
    try {
      setIsLoadingInboxes(true)
      const res = await fetchWithAuth('/api/agents/email/auto-reply/generate-prompt')
      if (!res.ok) throw new Error('Failed to fetch data')
      const data = await res.json()
      setInboxes(data.inboxes || [])
      setSystemPrompt(data.system_prompt || '')
      setPromptSource(data.prompt_source || 'default')

      // Check for cached prompt
      if (data.cached_prompt) {
        setCachedPrompt(data.cached_prompt)
      }

      if (data.inboxes?.length > 0 && !selectedInboxId) {
        setSelectedInboxId(data.inboxes[0].id)
      }
    } catch (error) {
      console.error('Failed to fetch inboxes:', error)
      toast.error(tUi("failedToLoadEmailInboxes"))
    } finally {
      setIsLoadingInboxes(false)
    }
  }

  const fetchEmailCount = useCallback(async () => {
    if (!selectedInboxId || !dateFrom) return

    try {
      setIsLoadingCount(true)
      const params = new URLSearchParams({
        inbox_id: selectedInboxId,
        date_from: dateFrom,
      })
      if (dateTo) params.set('date_to', dateTo)

      const res = await fetchWithAuth(`/api/agents/email/auto-reply/generate-prompt?${params}`)
      if (!res.ok) throw new Error('Failed to fetch count')
      const data = await res.json()
      setEmailCountPreview({
        count: data.email_count,
        hasMore: data.has_more || false,
      })
    } catch (error) {
      console.error('Failed to fetch email count:', error)
      setEmailCountPreview(null)
    } finally {
      setIsLoadingCount(false)
    }
  }, [selectedInboxId, dateFrom, dateTo, fetchWithAuth])

  const handleStartAnalysis = async () => {
    if (!selectedInboxId) {
      toast.error(tUi("pleaseSelectAnInbox"))
      return
    }

    if (!dateFrom) {
      toast.error(tUi("pleaseSelectAStartDate"))
      return
    }

    setStep('prompt')
  }

  const handleUseCachedPrompt = () => {
    if (cachedPrompt) {
      setGeneratedPrompt(cachedPrompt.prompt)
      setGeneratedStats(cachedPrompt.stats)
      setStep('result')
    }
  }

  const handleGeneratePrompt = async () => {
    try {
      setIsGenerating(true)

      const res = await fetchWithAuth('/api/agents/email/auto-reply/generate-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inbox_id: selectedInboxId,
          date_from: dateFrom,
          date_to: dateTo || undefined,
          max_emails: maxEmails ? parseInt(maxEmails) : 1000,
          system_prompt: systemPrompt,
        }),
      })

      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.error || 'Failed to generate prompt')
      }

      const data = await res.json()
      setGeneratedPrompt(data.prompt)
      setGeneratedStats(data.stats)

      // Update cached prompt
      setCachedPrompt({
        prompt: data.prompt,
        stats: data.stats,
        generatedAt: new Date().toISOString(),
        expiresAt: data.cached?.expiresAt || '',
        inboxId: selectedInboxId,
      })

      setStep('result')
      toast.success(tUi("promptGeneratedSuccessfully"))
    } catch (error) {
      console.error('Failed to generate prompt:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to generate prompt')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleApplyPrompt = () => {
    onApplyPrompt(generatedPrompt)
    toast.success(tUi("promptAppliedToAutoReplySettings"))
    handleClose()
  }

  const handleBackToConfig = () => {
    setStep('config')
  }

  const handleBackToPrompt = () => {
    setStep('prompt')
  }

  const handleClose = () => {
    setStep('config')
    onOpenChange(false)
  }

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)

    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins} min ago`
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    return date.toLocaleDateString(uiFormattingLocale)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose} modal={false}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {(step === 'prompt' || step === 'result') && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 mr-1"
                onClick={step === 'result' ? handleBackToPrompt : handleBackToConfig}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <Sparkles className="h-5 w-5" />
            <span>{step === 'config' && 'Generate Prompt from Emails'}</span>
            <span>{step === 'prompt' && 'Review System Prompt'}</span>
            <span>{step === 'result' && 'Generated Prompt'}</span>
          </DialogTitle>
          <DialogDescription>
            <span>{step === 'config' && 'Analyze email conversations to create an effective auto-reply prompt.'}</span>
            <span>{step === 'prompt' && 'Review the AI instructions that will be used to generate your prompt.'}</span>
            <span>{step === 'result' && 'Review the generated prompt and apply it to your auto-reply settings.'}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Configuration */}
        {step === 'config' && (
          <div className="space-y-6 py-4">
            {/* Cached Prompt Notice */}
            {cachedPrompt && (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-green-700"><span key={tUi("previouslyGeneratedPromptAvailable")}>{tUi("previouslyGeneratedPromptAvailable")}</span></span>
                    <div className="text-xs text-green-600 mt-1">
                      <span><span key={tUi("generated")}>{tUi("generated")}</span></span> {formatTimeAgo(cachedPrompt.generatedAt)} <span>•</span> {cachedPrompt.stats.threadsAnalyzed} <span><span key={tUi("threadsAnalyzed")}>{tUi("threadsAnalyzed")}</span></span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-4 border-green-300 text-green-700 hover:bg-green-100"
                    onClick={handleUseCachedPrompt}
                  >
                    <span key={tUi("usePrevious")}>{tUi("usePrevious")}</span></Button>
                </AlertDescription>
              </Alert>
            )}

            {/* Inbox Selection */}
            <div className="space-y-2">
              <Label><span key={tUi("selectInbox")}>{tUi("selectInbox")}</span></Label>
              {isLoadingInboxes ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span><span key={tUi("loadingInboxes")}>{tUi("loadingInboxes")}</span></span>
                </div>
              ) : inboxes.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <span key={tUi("noEmailInboxesFoundPleaseConnectAnEmail")}>{tUi("noEmailInboxesFoundPleaseConnectAnEmail")}</span></AlertDescription>
                </Alert>
              ) : (
                <Select value={selectedInboxId} onValueChange={setSelectedInboxId}>
                  <SelectTrigger>
                    <SelectValue placeholder={tUi("selectAnInbox")} />
                  </SelectTrigger>
                  <SelectContent>
                    {inboxes.map((inbox) => (
                      <SelectItem key={inbox.id} value={inbox.id}>
                        {inbox.address}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span><span key={tUi("startDate")}>{tUi("startDate")}</span></span>
                </Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span><span key={tUi("endDateOptional")}>{tUi("endDateOptional")}</span></span>
                </Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  min={dateFrom}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            {/* Email Count Preview */}
            {selectedInboxId && dateFrom && (
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                <FileText className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-700">
                  {isLoadingCount ? (
                    <span className="flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span key={tUi("countingEmails")}>{tUi("countingEmails")}</span></span>
                  ) : emailCountPreview !== null ? (
                    <><TranslationText>{tUi.rich("tag0Value0Value1Tag0EmailsFoundInSelected", { value0: String(emailCountPreview.count.toLocaleString(uiFormattingLocale)), value1: String(emailCountPreview.hasMore ? '+' : ''), tag0: chunks => (<strong>{chunks}</strong>) })}</TranslationText></>
                  ) : (
                    tUi("selectDatesToSeeEmailCount")
                  )}
                </span>
                {emailCountPreview !== null && !isLoadingCount && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto h-6 px-2"
                    onClick={fetchEmailCount}
                  >
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                )}
              </div>
            )}

            {/* Max Emails */}
            <div className="space-y-2">
              <Label><span key={tUi("maximumEmails")}>{tUi("maximumEmails")}</span></Label>
              <Input
                type="number"
                placeholder="1000"
                value={maxEmails}
                onChange={(e) => setMaxEmails(e.target.value)}
                min={10}
                max={1000}
              />
              <p className="text-xs text-gray-500">
                <span key={tUi("default1000SystemWillAnalyzeEmailsToCreate")}>{tUi("default1000SystemWillAnalyzeEmailsToCreate")}</span></p>
            </div>

            {/* Info */}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <span><span key={tUi("theAIWillAnalyzeYourEmailConversationsTo")}>{tUi("theAIWillAnalyzeYourEmailConversationsTo")}</span></span>
                <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                  <li><span key={tUi("matchesYourCompanySCommunicationStyle")}>{tUi("matchesYourCompanySCommunicationStyle")}</span></li>
                  <li><span key={tUi("includesRelevantProductServiceInformation")}>{tUi("includesRelevantProductServiceInformation")}</span></li>
                  <li><span key={tUi("handlesCommonCustomerInquiriesEffectively")}>{tUi("handlesCommonCustomerInquiriesEffectively")}</span></li>
                  <li><span key={tUi("usesAppropriateGreetingsAndSignatures")}>{tUi("usesAppropriateGreetingsAndSignatures")}</span></li>
                </ul>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Step 2: System Prompt Review */}
        {step === 'prompt' && (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Settings2 className="h-4 w-4" />
              <span><TranslationText>{tUi.rich("sourceValue0", { value0: String(promptSource === 'tenant' ? tUi("tenantConfig") : promptSource === 'global' ? tUi("globalConfig") : tUi("default")) })}</TranslationText></span>
              <Badge variant="outline" className="text-xs">
                <span key={tUi("editable")}>{tUi("editable")}</span></Badge>
            </div>

            <div className="space-y-2">
              <Label><span key={tUi("systemPromptForGeneration")}>{tUi("systemPromptForGeneration")}</span></Label>
              <Textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                className="min-h-[300px] font-mono text-sm"
                placeholder={tUi("enterSystemPromptForAI")}
              />
              <p className="text-xs text-gray-500">
                <span key={tUi("thisPromptInstructsTheAIOnHowTo")}>{tUi("thisPromptInstructsTheAIOnHowTo")}</span></p>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                <strong><span key={tUi("selectedConfiguration")}>{tUi("selectedConfiguration")}</span></strong>
                <ul className="list-disc list-inside mt-1">
                  <li><span><span key={tUi("inbox")}>{tUi("inbox")}</span></span> {inboxes.find(i => i.id === selectedInboxId)?.address}</li>
                  <li><span><span key={tUi("dateRange")}>{tUi("dateRange")}</span></span> {dateFrom} <span key={dateTo ? tUi("toValue0", { value0: String(dateTo) }) : tUi("onwards")}>{dateTo ? tUi("toValue0", { value0: String(dateTo) }) : tUi("onwards")}</span></li>
                  <li><span><span key={tUi("maxEmails")}>{tUi("maxEmails")}</span></span> {maxEmails || '1000'}</li>
                </ul>
              </AlertDescription>
            </Alert>

            {/* Progress indicator for generating */}
            {isGenerating && (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                  <div>
                    <div className="font-medium text-purple-700"><span key={tUi("generatingPrompt")}>{tUi("generatingPrompt")}</span></div>
                    <div className="text-sm text-purple-600">
                      <span key={tUi("analyzingEmailsAndCreatingAutoReplyPromptThis")}>{tUi("analyzingEmailsAndCreatingAutoReplyPromptThis")}</span></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Result */}
        {step === 'result' && (
          <div className="space-y-4 py-4">
            {/* Stats */}
            {generatedStats && (
              <div className="flex items-center gap-4 p-3 bg-green-50 rounded-lg text-sm">
                <div className="flex items-center gap-1 text-green-700">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="font-medium"><span key={tUi("generatedSuccessfully")}>{tUi("generatedSuccessfully")}</span></span>
                </div>
                <div className="text-green-600">
                  {generatedStats.emailsProcessed} <span><span key={tUi("emails")}>{tUi("emails")}</span></span> {generatedStats.threadsAnalyzed} <span><span key={tUi("threadsAnalyzed")}>{tUi("threadsAnalyzed")}</span></span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label><span key={tUi("generatedAutoReplyPrompt")}>{tUi("generatedAutoReplyPrompt")}</span></Label>
              <Textarea
                value={generatedPrompt}
                onChange={(e) => setGeneratedPrompt(e.target.value)}
                className="min-h-[350px] font-mono text-sm"
                placeholder={tUi("generatedPromptWillAppearHere")}
              />
              <p className="text-xs text-gray-500">
                <span key={tUi("youCanEditThisPromptBeforeApplyingIt")}>{tUi("youCanEditThisPromptBeforeApplyingIt")}</span></p>
            </div>

            <Alert className="bg-blue-50 border-blue-200">
              <Mail className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-700">
                <span key={tUi("clickApplyToAutoReplyToUseThis")}>{tUi("clickApplyToAutoReplyToUseThis")}</span></AlertDescription>
            </Alert>
          </div>
        )}

        <DialogFooter>
          {step === 'config' && (
            <>
              <Button variant="outline" onClick={handleClose}>
                <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
              <Button
                onClick={handleStartAnalysis}
                disabled={!selectedInboxId || !dateFrom || inboxes.length === 0}
              >
                <span key={tUi("nextReviewPrompt")}>{tUi("nextReviewPrompt")}</span></Button>
            </>
          )}
          {step === 'prompt' && (
            <>
              <Button variant="outline" onClick={handleBackToConfig}>
                <span key={tUi("back")}>{tUi("back")}</span></Button>
              <Button
                onClick={handleGeneratePrompt}
                disabled={isGenerating || !systemPrompt.trim()}
              >
                {isGenerating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <span key={isGenerating ? tUi("generating") : tUi("generatePrompt")}>{isGenerating ? tUi("generating") : tUi("generatePrompt")}</span>
              </Button>
            </>
          )}
          {step === 'result' && (
            <>
              <Button variant="outline" onClick={handleBackToPrompt}>
                <span key={tUi("regenerate")}>{tUi("regenerate")}</span></Button>
              <Button
                onClick={handleApplyPrompt}
                disabled={!generatedPrompt.trim()}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                <span><span key={tUi("applyToAutoReply")}>{tUi("applyToAutoReply")}</span></span>
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
